import { useState, useEffect, useRef } from "react";
import { Upload, X, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { DEFAULT_THEME, FONT_OPTIONS } from "../../lib/theme";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const COLOR_FIELDS = [
  { key: "primary_color", label: "Primary / Button Color" },
  { key: "button_text_color", label: "Button Text Color" },
  { key: "secondary_color", label: "Secondary / Accent Color" },
  { key: "background_color", label: "Background Color" },
  { key: "text_color", label: "Text Color" },
];

const LAYOUTS = [
  { value: "left", label: "Classic — glass card, left aligned" },
  { value: "center", label: "Centered — glass card in middle" },
  { value: "minimal", label: "Minimal — text on image, no card" },
];

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 border border-black/10 cursor-pointer bg-white" data-testid={`${testid}-picker`} />
      <div className="flex-1">
        <Label className="text-xs uppercase tracking-widest">{label}</Label>
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-8 text-sm" data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <div className="flex items-center gap-3 mt-2">
        <div className="w-24 h-16 border border-black/10 bg-muted flex items-center justify-center overflow-hidden">
          {value ? <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-muted-foreground">Default</span>}
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300 flex items-center gap-2"
          data-testid={`${testid}-upload`}>
          <Upload strokeWidth={1.5} size={14} /> {uploading ? "Uploading..." : "Upload"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="p-2 hover:text-destructive transition-colors duration-300" data-testid={`${testid}-clear`} aria-label="Remove">
            <X strokeWidth={1.5} size={16} />
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid={`${testid}-file`} />
    </div>
  );
}

export default function AdminTheme() {
  const { refresh } = useTheme();
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setTheme({ ...DEFAULT_THEME, ...(r.data.theme || {}) });
    }).catch(() => {});
  }, []);

  if (!theme) return <div className="h-40 bg-muted animate-pulse" />;

  const set = (k, v) => setTheme((t) => ({ ...t, [k]: v }));

  const moveSection = (i, dir) => {
    const next = [...theme.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set("sections", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { theme });
      await refresh();
      toast.success("Theme applied across the website");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setTheme({ ...DEFAULT_THEME, sections: DEFAULT_THEME.sections.map((s) => ({ ...s })) });

  return (
    <div data-testid="admin-theme">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-3xl font-semibold">Theme Customizer</h1>
        <button onClick={reset} className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-gold transition-colors duration-300" data-testid="theme-reset">
          <RotateCcw strokeWidth={1.5} size={14} /> Reset to Defaults
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-8">Change colors, fonts, logo, banners and homepage layout. Changes apply instantly across the website after saving.</p>

      <div className="flex flex-col gap-6">
        <div className="bg-white border border-black/10 p-6">
          <p className="small-caps text-gold mb-6">Colors</p>
          <div className="grid md:grid-cols-2 gap-6">
            {COLOR_FIELDS.map((f) => (
              <ColorField key={f.key} label={f.label} value={theme[f.key]} onChange={(v) => set(f.key, v)} testid={`theme-${f.key.replace(/_/g, "-")}`} />
            ))}
          </div>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <p className="small-caps text-gold mb-6">Fonts</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-widest">Heading Font</Label>
              <Select value={theme.heading_font} onValueChange={(v) => set("heading_font", v)}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="theme-heading-font"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.heading.map((f) => <SelectItem key={f} value={f} data-testid={`heading-font-${f.replace(/ /g, "-").toLowerCase()}`}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="font-serif text-2xl mt-3" style={{ fontFamily: theme.heading_font }}>Preview Heading</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Body Font</Label>
              <Select value={theme.body_font} onValueChange={(v) => set("body_font", v)}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="theme-body-font"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.body.map((f) => <SelectItem key={f} value={f} data-testid={`body-font-${f.replace(/ /g, "-").toLowerCase()}`}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-sm mt-3" style={{ fontFamily: theme.body_font }}>Preview body text for product descriptions and pages.</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <p className="small-caps text-gold mb-6">Logo & Banner Images</p>
          <div className="grid md:grid-cols-3 gap-6">
            <ImageField label="Logo (replaces text logo)" value={theme.logo_url} onChange={(v) => set("logo_url", v)} testid="theme-logo" />
            <ImageField label="Homepage Hero Banner" value={theme.hero_image} onChange={(v) => set("hero_image", v)} testid="theme-hero" />
            <ImageField label="Heritage Section Image" value={theme.heritage_image} onChange={(v) => set("heritage_image", v)} testid="theme-heritage" />
          </div>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <p className="small-caps text-gold mb-6">Homepage Layout</p>
          <Label className="text-xs uppercase tracking-widest">Hero Style</Label>
          <Select value={theme.hero_layout} onValueChange={(v) => set("hero_layout", v)}>
            <SelectTrigger className="mt-2 rounded-sm max-w-md" data-testid="theme-hero-layout"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LAYOUTS.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`layout-${l.value}`}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <p className="small-caps text-gold mb-6">Homepage Sections — Show / Hide & Reorder</p>
          <div className="flex flex-col divide-y divide-black/5">
            {theme.sections.map((s, i) => (
              <div key={s.id} className="flex items-center gap-4 py-3" data-testid={`section-row-${s.id}`}>
                <Switch checked={s.visible} onCheckedChange={(v) => set("sections", theme.sections.map((x) => x.id === s.id ? { ...x, visible: v } : x))} data-testid={`section-toggle-${s.id}`} />
                <span className="flex-1 text-sm">{s.label}</span>
                <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`section-up-${s.id}`} aria-label="Move up"><ArrowUp strokeWidth={1.5} size={14} /></button>
                <button type="button" onClick={() => moveSection(i, 1)} disabled={i === theme.sections.length - 1} className="p-1.5 border border-black/10 hover:border-gold disabled:opacity-30 transition-colors duration-300" data-testid={`section-down-${s.id}`} aria-label="Move down"><ArrowDown strokeWidth={1.5} size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving}
          className="bg-[#111] text-[#FAF9F6] py-4 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
          data-testid="theme-save">
          {saving ? "Applying..." : "Save & Apply Theme"}
        </button>
      </div>
    </div>
  );
}
